import React from "react";
import { updateOrCreateCampaign } from "../../../store/api/calls";
import DataTable from "../../theme/tables/DataTable";
import BaseModal from "../../theme/modals/BaseModal";
import BaseForm from "../../theme/forms/BaseForm";
import api from "../../../store/api/api";
import { Button, Grid, LinearProgress } from "@mui/material";

class CampaignsTab extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      editModalOpen: false,
      editModalErrorMsg: null,
      deleteModalOpen: false,
      editingInstance: {},
      formInputs: [],
      deactivatedCampaignView: false,
      campaigns: [],
      inactiveCampaigns: [],
      fetchLoading: false,
    };
  }

  componentDidMount() {
    this.fetchCampaigns();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.focusedCampaign !== this.props.focusedCampaign && this.props.focusedCampaign) {
      this.onOpenEditModal(this.props.focusedCampaign);
    }
  }

  fetchCampaigns = (showInactive = this.state.deactivatedCampaignView) => {
    this.setState({ fetchLoading: true });
    const params = showInactive ? { deactivated: 1 } : {};

    return api
      .get("/campaigns", params)
      .then((res) => {
        const sorted = [...res.data].sort((a, b) => {
          if (a.name < b.name) return -1;
          if (a.name > b.name) return 1;
          return 0;
        });
        if (showInactive) {
          this.setState({ inactiveCampaigns: sorted });
        } else {
          this.setState({ campaigns: sorted });
        }
      })
      .catch(() => {
        this.props.setError("Failed to fetch campaigns");
      })
      .finally(() => {
        this.setState({ fetchLoading: false });
      });
  };

  toggleCampaignView = () => {
    const showInactive = !this.state.deactivatedCampaignView;
    this.setState({ deactivatedCampaignView: showInactive }, () => {
      this.fetchCampaigns(showInactive);
    });
  };

  _filterKeysForCampaignDataTable = () => {
    const campaigns = this.state.deactivatedCampaignView
      ? this.state.inactiveCampaigns
      : this.state.campaigns;

    return campaigns.map((d) => ({
      name: d.name,
      description: d.description,
      _data: d,
    }));
  };

  _generateFormInputs = (modelInstance) => {
    return [
      {
        type: "text",
        title: "Name",
        dataKey: "name",
        value: modelInstance?.name,
        placeholder: "Client",
      },
      {
        type: "text",
        title: "Description",
        dataKey: "description",
        value: modelInstance?.description,
        placeholder: "Where is it and what are we doing there?",
      },
    ];
  };

  onOpenEditModal = (modelInstance = {}, creating = false) => {
    this.setState({
      formInputs: this._generateFormInputs(modelInstance),
      editModalOpen: true,
      editingInstance: modelInstance,
    });
  };

  onCloseEditModal = () => {
    this.setState({
      editModalOpen: false,
      editingInstance: {},
      editModalErrorMsg: null,
    });
  };

  onOpenDeleteModal = (modelInstance = {}) => {
    this.setState({ deleteModalOpen: true, editingInstance: modelInstance });
  };

  onCloseDeleteModal = () => {
    this.setState({ deleteModalOpen: false, editingInstance: {} });
  };

  handleUpdateCreateSubmission = (data) => {
    const id = this.state.editingInstance.id;
    if (id) data = { ...data, id };

    updateOrCreateCampaign(data)
      .then(() => {
        this.onCloseEditModal();
        this.props.showSuccess();
        this.fetchCampaigns();
      })
      .catch((err) => {
        const message = err?.response?.data?.error;
        this.props.setError(
          message || (id ? "Failed to update campaign" : "Failed to create campaign")
        );
      });
  };

  handleRemoveSubmission = () => {
    const id = this.state.editingInstance.id;
    updateOrCreateCampaign({ is_active: false, id })
      .then(() => {
        this.onCloseDeleteModal();
        this.fetchCampaigns();
      })
      .catch(() => {
        this.props.setError("Failed to deactivate campaign");
      });
  };

  handleReactivateSubmission = () => {
    const id = this.state.editingInstance.id;
    updateOrCreateCampaign({ is_active: true, id })
      .then(() => {
        this.onCloseDeleteModal();
        this.fetchCampaigns();
      })
      .catch(() => {
        this.props.setError("Failed to reactivate campaign");
      });
  };

  render() {
    const { canViewInactive, is_superuser } = this.props;
    const { deactivatedCampaignView, editingInstance } = this.state;

    const tableActions = deactivatedCampaignView
      ? [
          {
            title: "edit",
            icon: "fa-edit",
            action: (modelInstance) => this.onOpenEditModal(modelInstance),
          },
          {
            title: "Reactivate",
            icon: "fa-plus-square",
            action: this.onOpenDeleteModal,
          },
        ]
      : [
          {
            title: "edit",
            icon: "fa-edit",
            action: (modelInstance) => this.onOpenEditModal(modelInstance),
          },
          {
            title: "Remove",
            icon: "fa-minus-square",
            action: this.onOpenDeleteModal,
          },
        ];

    return (
      <div>
        <div className="button-table-top-wrapper">
          <Grid container spacing={2} sx={{ marginBottom: "10px" }}>
            {is_superuser && (
              <Grid item>
                <a className="button" onClick={() => this.onOpenEditModal({}, true)}>
                  + Add Campaign
                </a>
              </Grid>
            )}
            {canViewInactive && (
              <Grid item>
                {!deactivatedCampaignView ? (
                  <Button
                    variant="contained"
                    color="secondary"
                    disableElevation={true}
                    startIcon={<i className="fas fa-minus-square" />}
                    onClick={this.toggleCampaignView}
                  >
                    Show Inactive Campaigns
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    color="secondary"
                    disableElevation={true}
                    startIcon={<i className="fas fa-plus-square" />}
                    onClick={this.toggleCampaignView}
                  >
                    Show Active Campaigns
                  </Button>
                )}
              </Grid>
            )}
          </Grid>
        </div>

        {this.state.fetchLoading && <LinearProgress sx={{ marginBottom: "10px" }} />}

        <DataTable
          data={this._filterKeysForCampaignDataTable()}
          title="Campaigns"
          actions={tableActions}
        />

        <BaseModal
          open={this.state.editModalOpen}
          onClose={this.onCloseEditModal}
          center
        >
          <BaseForm
            inputs={this.state.formInputs}
            onCancel={this.onCloseEditModal}
            onSubmit={this.handleUpdateCreateSubmission}
          />
          {!!this.state.editModalErrorMsg && (
            <div className="danger-text flx center">
              {this.state.editModalErrorMsg}
            </div>
          )}
        </BaseModal>

        <BaseModal
          open={this.state.deleteModalOpen}
          onClose={this.onCloseDeleteModal}
          center
        >
          <div className="modal-internal-wrapper">
            <p>
              Are you sure you want to {deactivatedCampaignView ? "reactivate" : "remove"} the campaign{" "}
              {this.state.editingInstance.name}?
            </p>
            <div className="button-wrapper">
              <div
                className="button"
                onClick={deactivatedCampaignView ? this.handleReactivateSubmission : this.handleRemoveSubmission}
              >
                {deactivatedCampaignView ? "Reactivate" : "Remove"}
              </div>
              <div
                className="button button-outline"
                onClick={this.onCloseDeleteModal}
              >
                Cancel
              </div>
            </div>
          </div>
        </BaseModal>
      </div>
    );
  }
}

export default CampaignsTab;
